/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import type { Collection, Node } from '@react-types/shared';
import * as stylex from '@stylexjs/stylex';
import { $getRoot } from 'lexical';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import type {
  TextFieldProps
} from 'react-aria-components';
import {
  CollectionBuilder,
  FieldErrorContext,
  ListStateContext,
  OverlayTriggerStateContext,
  Provider,
  SelectableCollectionContext,
  useContextProps,
  type ContextValue,
} from 'react-aria-components';
import { createPortal } from 'react-dom';
import { useListState, useOverlayTriggerState } from 'react-stately';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { ChatTextInputContext } from '../chat-text-input/chat-text-input.component';
import { HintContext } from '../hint/hint.component';
import type { LabelProps } from '../label/label.component';
import { LabelContext } from '../label/label.component';
import { ListBoxContext } from '../list-box/list-box.component';
import { PopoverContext } from '../popover/popover.component';
import type {
  AnyFeature,
  ChatFeature,
  ExtractTriggers,
  InsertFn,
} from './chat-text-field.types';
import { ControlledValuePlugin } from './controlled-value-plugin.component';
import { FeatureNode } from './feature.node';
import { ListBoxNavigationPlugin } from './listbox-navigation-plugin.component';
import { TriggerDetectionPlugin } from './trigger-detection-plugin.component';

interface ChatTextFieldProps<P extends readonly AnyFeature[]> extends Omit<TextFieldProps, 'children' | 'className' | 'style'> {
  /** The size variant of the chat text field. (Default: 52) */
  size?: keyof typeof sizeVariants;
  /** Whether the chat text field is fully rounded. (Default: false) */
  isRounded?: boolean;
  /**
   * Feature descriptors.  Each feature registers its trigger character and
   * provides the `renderer` used to display inserted nodes.
   */
  features?: P;
  children?: ReactNode | ((renderProps: ChatTextFieldRenderProps<P>) => ReactNode);
  style?: StyleOrFunction<ChatTextFieldRenderProps<P>>;
}

interface ChatTextFieldRenderProps<P extends readonly AnyFeature[]> {
  /**
   * The trigger character that is currently active (e.g. `'@'`), or `null` when no trigger is active.
   */
  activeTrigger: ExtractTriggers<P> | null;
  /**
   * The text the user has typed *after* the trigger character, used to filter
   * suggestion lists (e.g. `'ali'` after `'@ali'`).
   */
  triggerQuery: string;
  /**
   * Inserts the node produced by the matching feature's `renderer`, automatically
   * deleting the trigger character and any query text first.
   */
  insert: InsertFn<P>;
  /** Closes the active trigger state (same as pressing Escape). */
  close: () => void;
}

const ChatTextFieldContext = createContext<ContextValue<ChatTextFieldProps<readonly AnyFeature[]>, never>>({});

/**
 * Set to `true` on the content passed to `CollectionBuilder.content` (the hidden
 * pre-render pass used to extract ListBox items).  `ChatTextInput` reads this
 * context and returns `null` when hidden, preventing `PlainTextPlugin` from
 * mounting a second time and registering a second Lexical decorator listener.
 *
 * We cannot use `useIsHidden()` from `@react-aria/collections` for this because
 * `react-aria-components` bundles its own copy of that package (a different version
 * with a different `HiddenContext` object), so the two packages do not share the
 * same React context instance and `useIsHidden()` always returns `false`.
 */
const ChatTextFieldHiddenContext = createContext(false);

function ChatTextField<const P extends readonly AnyFeature[]>(props: ChatTextFieldProps<P>) {
  [props] = useContextProps(props, null as never, ChatTextFieldContext);

  const {
    features = EMPTY_FEATURES as unknown as P,
    onChange,
    value,
    children,
    size = 52,
    isRounded = false,
    isDisabled = false,
    isInvalid: isInvalidProp = false,
    isReadOnly = false,
    validate,
  } = props;

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!validate) return;
    const result = validate(value ?? '');
    if (!result || result === true) {
      setValidationErrors([]);
    } else if (typeof result === 'string') {
      setValidationErrors([result]);
    } else {
      setValidationErrors(result);
    }
  }, [value, validate]);

  const isInvalid = isInvalidProp || validationErrors.length > 0;

  const fieldErrorContextValue = useMemo(
    () => ({
      isInvalid,
      validationErrors,
      validationDetails: {
        badInput: false,
        customError: validationErrors.length > 0,
        patternMismatch: false,
        rangeOverflow: false,
        rangeUnderflow: false,
        stepMismatch: false,
        tooLong: false,
        tooShort: false,
        typeMismatch: false,
        valueMissing: false,
        valid: !isInvalid,
      },
    }),
    [isInvalid, validationErrors],
  );

  const [activeTrigger, setActiveTrigger] = useState<ExtractTriggers<P> | null>(null);
  const [triggerQuery, setTriggerQuery] = useState('');

  const [insert, setInsert] = useState<InsertFn<readonly AnyFeature[]>>(() => () => void 0);

  const overlayTriggerState = useOverlayTriggerState({ isOpen: activeTrigger !== null });
  const caretAnchorRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLElement>(null);

  const onClose = useCallback(() => {
    setActiveTrigger(null);
    setTriggerQuery('');
  }, []);

  const onTriggerChange = useCallback((trigger: ExtractTriggers<P> | null, query: string) => {
    setActiveTrigger(trigger);
    setTriggerQuery(query);
  }, []);

  FeatureNode.setFeatures(features);

  const chatTextInputContextValue = useMemo(
    () => ({ size, isRounded, isDisabled, isInvalid, isReadOnly }),
    [size, isRounded, isDisabled, isInvalid, isReadOnly],
  );

  const labelContextValue = useMemo(
    () => ({ size: LABEL_SIZES[size], color: isDisabled ? ('disabled' as const) : ('primary' as const) }),
    [size, isDisabled],
  );

  const renderedChildren = typeof children === 'function' ? children({ activeTrigger, triggerQuery, insert, close: onClose }) : children;

  const hiddenPassContent = (
    <ChatTextFieldHiddenContext.Provider value>
      {renderedChildren}
    </ChatTextFieldHiddenContext.Provider>
  );

  return (
    <div {...stylex.props(styles.base, sizeVariants[size])}>
      <LexicalComposer
        initialConfig={{
          namespace: 'ChatTextField',
          nodes: [FeatureNode],
          onError: (error: Error) => { throw error; },
          theme: { paragraph: stylex.props(paragraphStyles.base).className },
        }}>
        {createPortal(
          <span ref={caretAnchorRef} style={{ height: 0, left: 0, pointerEvents: 'none', position: 'fixed', top: 0, width: 0 }} />,
          document.body,
        )}

        <OnChangePlugin
          onChange={(editorState) => editorState.read(() => onChange?.($getRoot().getTextContent()))}
          ignoreSelectionChange
        />
        {value !== undefined && <ControlledValuePlugin value={value} isReadOnly={onChange === undefined} />}

        <Provider
          values={[
            [OverlayTriggerStateContext, overlayTriggerState],
            [ChatTextInputContext, chatTextInputContextValue],
            [LabelContext, labelContextValue],
            [HintContext, { style: hintSizeVariants[size] }],
            [FieldErrorContext, fieldErrorContextValue],
          ]}>
          <CollectionBuilder content={hiddenPassContent}>
            {(collection: Collection<Node<object>>) => (
              <ChatTextFieldInner
                collection={collection}
                features={features}
                activeTrigger={activeTrigger}
                triggerQuery={triggerQuery}
                onTriggerChange={onTriggerChange as (trigger: string | null, query: string) => void}
                onClose={onClose}
                caretAnchorRef={caretAnchorRef}
                popoverRef={popoverRef}
                setInsert={setInsert}>
                {renderedChildren}
              </ChatTextFieldInner>
            )}
          </CollectionBuilder>
        </Provider>
      </LexicalComposer>
    </div>
  );
}

interface ChatTextFieldInnerProps {
  collection: Collection<Node<object>>;
  children: ReactNode;
  features: readonly AnyFeature[];
  activeTrigger: string | null;
  size?: keyof typeof sizeVariants;
  triggerQuery: string;
  onTriggerChange: (trigger: string | null, query: string) => void;
  onClose: () => void;
  caretAnchorRef: React.RefObject<HTMLSpanElement | null>;
  popoverRef: React.RefObject<HTMLElement | null>;
  setInsert: (fn: InsertFn<readonly AnyFeature[]>) => void;
}

function ChatTextFieldInner({
  collection,
  children,
  features,
  activeTrigger,
  triggerQuery,
  onTriggerChange,
  onClose,
  caretAnchorRef,
  popoverRef,
  setInsert,
  size,
}: ChatTextFieldInnerProps) {
  const listState = useListState({ collection, selectionMode: 'none', children: undefined });

  const hasItems = listState.collection.size > 0;

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <ListStateContext.Provider value={listState as any}>
      <SelectableCollectionContext.Provider value={VIRTUAL_FOCUS_CONTEXT}>
        <Provider values={[
          [ListBoxContext, { style: hasItems ? listBoxStyles.base : listBoxStyles.empty }],
          [PopoverContext, {
            triggerRef: caretAnchorRef,
            isNonModal: true,
            ref: popoverRef,
            isOpen: !!activeTrigger && hasItems,
            offset: size === 52 ? 34 : 17
          }],
        ]}>
          <TriggerDetectionPlugin
            features={features}
            activeTrigger={activeTrigger}
            triggerQuery={triggerQuery}
            onTriggerChange={onTriggerChange}
            onClose={onClose}
            caretAnchorRef={caretAnchorRef}
            popoverRef={popoverRef}
            setInsert={setInsert}
          />
          <ListBoxNavigationPlugin
            activeTrigger={activeTrigger}
            triggerQuery={triggerQuery}
            listState={listState}
          />
          {children}
        </Provider>
      </SelectableCollectionContext.Provider>
    </ListStateContext.Provider>
  );
}

/**
 * Define a `ChatFeature` descriptor with accurate literal-type inference for
 * `id` and `trigger`, without requiring `as const` at the call site.
 *
 * @example
 * ```tsx
 * const mentionFeature = defineChatFeature({
 *   id: 'mention',
 *   trigger: '@',
 *   renderer: ({ name }: { name: string }) => <Mention>@{name}</Mention>,
 *   getTextContent: ({ name }) => `@${name}`,
 * });
 * ```
 */
function defineChatFeature<
  const Id extends string,
  const Trigger extends string,
  TProps extends Record<string, unknown>,
>(feature: ChatFeature<Id, Trigger, TProps>): ChatFeature<Id, Trigger, TProps> {
  return feature;
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column'
  },
});

const paragraphStyles = stylex.create({
  base: {
    margin: 0,
  },
});

const listBoxStyles = stylex.create({
  base: {
    padding: 4,
    borderRadius: 10,
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: $.shadowCenter070Loud,
  },
  empty: {
    padding: 0,
    borderRadius: 10,
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: $.shadowCenter070Loud,
  },
});

const LABEL_SIZES: Record<keyof typeof sizeVariants, LabelProps['size']> = {
  52: 16,
  40: 13,
};

const sizeVariants = stylex.create({
  52: { gap: 6 },
  40: { gap: 4 }
});

const hintSizeVariants = stylex.create({
  52: { marginTop: -2 },
  40: {}
});

// Passed to the ListBox subtree to suppress its own physical-focus and
// typeahead keyboard handling — focus is driven from the Lexical editor plugin.
const VIRTUAL_FOCUS_CONTEXT = { shouldUseVirtualFocus: true, disallowTypeAhead: true };

// Stable empty array used as the default for `features` to prevent a new array
// reference on every render — which would cause TriggerDetectionPlugin's
// useEffect (features in its dep array) to fire on every render → infinite loop.
const EMPTY_FEATURES = [] as unknown as readonly AnyFeature[];

const CHAT_TEXT_FIELD_SLOTS = {
  MIC_ICON_BUTTON: 'mic-icon-button',
  SEND_ICON_BUTTON: 'send-icon-button',
} as const;

export { CHAT_TEXT_FIELD_SLOTS, ChatTextField, ChatTextFieldContext, ChatTextFieldHiddenContext, defineChatFeature, type ChatTextFieldProps };
